import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaReceipt, FaPaperclip, FaTimes } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';

/**
 * Dépôt d'une note de frais avec son justificatif (Yros/Kavero#18).
 *
 * Second critère du ticket : sans justificatif, la soumission est bloquée
 * jusqu'à l'ajout du fichier. Le bouton reste inactif tant qu'aucun fichier
 * n'est choisi, et le formulaire dit pourquoi — un bouton gris sans
 * explication ne se comprend pas.
 *
 * Une note peut porter plusieurs reçus, cinq au plus (décision du 21/09/2026) :
 * chaque choix de fichiers s'ajoute à la liste au lieu de la remplacer, et
 * chaque reçu se retire avant l'envoi.
 *
 * L'écran ne vérifie que ce qu'il voit : un champ vide, une date future, un
 * fichier qui n'est manifestement pas un reçu ou qui pèse trop. Le reste — le
 * type réel du fichier, lu dans son contenu — est jugé par Odoo, dont le refus
 * s'affiche mot pour mot.
 *
 * Assemblage repris de `DemandeAvance` : mêmes classes, même bouton, même
 * conduite du succès et de l'erreur.
 */

// La liste fermée du socle (`NATURES_FRAIS`), dans le même ordre.
export const NATURES_FRAIS = ['transport', 'repas', 'hebergement', 'fournitures', 'autre'];

const TYPES_ACCEPTES = ['application/pdf', 'image/jpeg', 'image/png'];
const EXTENSIONS_ACCEPTEES = /\.(pdf|jpe?g|png)$/i;
const TAILLE_MAX = 5 * 1024 * 1024;
const NOMBRE_MAX = 5;
const LONGUEUR_MAX_DESCRIPTION = 500;

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

/** « AAAA-MM-JJ » du jour, en heure locale : la borne du champ date. */
function aujourdhui() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Un reçu se reconnaît à son type, ou à défaut à son extension : certains
 * navigateurs ne renseignent pas le type d'une photo prise au téléphone. */
function estUnRecu(fichier) {
  return TYPES_ACCEPTES.includes(fichier.type)
    || (!fichier.type && EXTENSIONS_ACCEPTEES.test(fichier.name));
}

/** « 850 ko », « 1,2 Mo » : la taille d'un fichier dans la langue de l'écran. */
function formaterTaille(octets, locale) {
  const [valeur, unite] = octets < 1024 * 1024
    ? [Math.max(1, Math.round(octets / 1024)), 'kilobyte']
    : [octets / (1024 * 1024), 'megabyte'];
  return new Intl.NumberFormat(locale, {
    style: 'unit', unit: unite, unitDisplay: 'short', maximumFractionDigits: 1,
  }).format(valeur);
}

function DemandeNoteFrais({ onDepot }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [date, setDate] = useState('');
  const [nature, setNature] = useState('');
  const [montant, setMontant] = useState('');
  const [description, setDescription] = useState('');
  const [justificatifs, setJustificatifs] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const champFichier = useRef(null);

  const verifier = () => {
    if (!date || !nature || !montant) return t('demandeNoteFrais.champsManquants');
    const montantNombre = Number(montant);
    if (!Number.isFinite(montantNombre) || montantNombre <= 0) return t('demandeNoteFrais.montantInvalide');
    // Comparaison de chaînes « AAAA-MM-JJ » : l'ordre lexical est l'ordre des dates.
    if (date > aujourdhui()) return t('demandeNoteFrais.dateFuture');
    if (description.trim().length > LONGUEUR_MAX_DESCRIPTION) return t('demandeNoteFrais.descriptionTropLongue');
    if (!justificatifs.length) return t('demandeNoteFrais.justificatifManquant');
    if (justificatifs.length > NOMBRE_MAX) return t('demandeNoteFrais.tropDeFichiers');
    const refuse = justificatifs.find((fichier) => !estUnRecu(fichier));
    if (refuse) return t('demandeNoteFrais.formatInvalide', { nom: refuse.name });
    const lourd = justificatifs.find((fichier) => fichier.size > TAILLE_MAX);
    if (lourd) return t('demandeNoteFrais.tropLourd', { nom: lourd.name });
    return null;
  };

  const ajouter = (event) => {
    const choisis = Array.from(event.target.files || []);
    setJustificatifs((actuels) => [
      ...actuels,
      ...choisis.filter((fichier) => !actuels.some((deja) =>
        deja.name === fichier.name && deja.size === fichier.size
        && deja.lastModified === fichier.lastModified)),
    ]);
    // Vidé après chaque choix : un reçu retiré doit pouvoir être rechoisi.
    event.target.value = '';
  };

  const retirer = (index) => {
    setJustificatifs((actuels) => actuels.filter((_, position) => position !== index));
  };

  const soumettre = async (event) => {
    event.preventDefault();
    setErreur(null);
    setSucces(null);
    const refus = verifier();
    if (refus) {
      setErreur(refus);
      return;
    }

    const formulaire = new FormData();
    formulaire.append('montant', montant);
    formulaire.append('nature', nature);
    formulaire.append('date_depense', date);
    if (description.trim()) formulaire.append('description', description.trim());
    justificatifs.forEach((fichier) => formulaire.append('justificatifs', fichier));

    setEnvoi(true);
    try {
      const reponse = await apiService.deposerNoteFrais(formulaire);
      if (reponse && reponse.ok) {
        setSucces(t('demandeNoteFrais.succes'));
        setDate('');
        setNature('');
        setMontant('');
        setDescription('');
        setJustificatifs([]);
        if (champFichier.current) champFichier.current.value = '';
        if (onDepot) onDepot();
      } else {
        setErreur(reponse?.message || t('demandeNoteFrais.erreurGenerique'));
      }
    } catch (e) {
      // Un refus métier remonte en 409 : le message d'Odoo s'affiche tel quel.
      setErreur(e?.response?.data?.message || t('demandeNoteFrais.erreurGenerique'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border border-gray-100 space-y-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <FaReceipt className="text-[--color-primary]" aria-hidden="true" />
          <h2 className="text-base sm:text-lg font-semibold">{t('demandeNoteFrais.titre')}</h2>
        </div>
        <p className="text-sm text-slate-600">{t('demandeNoteFrais.explication')}</p>
      </div>

      <form data-testid="note-frais-formulaire" onSubmit={soumettre} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="block text-sm font-medium">
            {t('demandeNoteFrais.date')}
            <input
              type="date"
              max={aujourdhui()}
              data-testid="note-frais-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block text-sm font-medium">
            {t('demandeNoteFrais.nature')}
            <select
              data-testid="note-frais-nature"
              value={nature}
              onChange={(e) => setNature(e.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">{t('demandeNoteFrais.natureChoisir')}</option>
              {NATURES_FRAIS.map((code) => (
                <option key={code} value={code}>{t(`notesFrais.natures.${code}`)}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium">
            {t('demandeNoteFrais.montant')}
            <input
              type="number"
              inputMode="decimal"
              min="1"
              step="any"
              data-testid="note-frais-montant"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
        </div>

        <label className="block text-sm font-medium">
          {t('demandeNoteFrais.description')}
          <textarea
            rows={2}
            maxLength={LONGUEUR_MAX_DESCRIPTION}
            data-testid="note-frais-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={CLASSE_CHAMP}
          />
          <span className="mt-1 block text-xs font-normal text-slate-500">{t('demandeNoteFrais.descriptionAide')}</span>
        </label>

        <div className="space-y-1">
          <label className="block text-sm font-medium" htmlFor="note-frais-justificatif">
            {t('demandeNoteFrais.justificatif')}
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={champFichier}
              id="note-frais-justificatif"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              data-testid="note-frais-justificatif"
              onChange={ajouter}
              className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
            />
          </div>
          <p className="text-xs text-slate-500">{t('demandeNoteFrais.justificatifAide')}</p>
          {justificatifs.length > 0 && (
            <ul data-testid="note-frais-pieces" className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {justificatifs.map((fichier, index) => (
                <li key={`${fichier.name}-${fichier.size}-${fichier.lastModified}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <FaPaperclip className="flex-shrink-0 text-slate-400" aria-hidden="true" />
                    <span className="break-all text-slate-700">{fichier.name}</span>
                    <span className="flex-shrink-0 text-xs text-slate-500 tabular-nums">{formaterTaille(fichier.size, locale)}</span>
                  </span>
                  <button
                    type="button"
                    data-testid={`note-frais-retirer-${index}`}
                    onClick={() => retirer(index)}
                    aria-label={t('demandeNoteFrais.retirer', { nom: fichier.name })}
                    title={t('demandeNoteFrais.retirer', { nom: fichier.name })}
                    className="flex-shrink-0 rounded p-2 text-slate-500 hover:bg-slate-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!justificatifs.length && (
            <p data-testid="note-frais-justificatif-manquant" className="flex items-center gap-1.5 text-xs text-amber-700">
              <FaPaperclip aria-hidden="true" />
              {t('demandeNoteFrais.justificatifManquant')}
            </p>
          )}
        </div>

        {erreur && (
          <p
            data-testid="note-frais-erreur"
            role="alert"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {erreur}
          </p>
        )}
        {succes && (
          <p
            data-testid="note-frais-succes"
            className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
          >
            {succes}
          </p>
        )}

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            data-testid="note-frais-soumettre"
            disabled={envoi || !justificatifs.length}
            className="flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FaPaperPlane size={14} aria-hidden="true" />
            {envoi ? t('demandeNoteFrais.envoi') : t('demandeNoteFrais.envoyer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DemandeNoteFrais;
